import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Snackbar,
  IconButton,
} from '@mui/material';
import {
  Search as SearchIcon,
  PersonOff as NotAccountedIcon,
  CheckCircle as AccountedIcon,
  Groups as TotalIcon,
  Refresh as RefreshIcon,
  FileDownload as ExportIcon,
} from '@mui/icons-material';
import { appColors } from '../Config/Theme';
import { IncidentRosterPerson, PersonStatus } from '../Types/Incident';
import { getUnaccountedPeople, getIncidentSummary, getIncidentDetail } from '../Services/IncidentService';
import AppBreadcrumbs from '../Components/Navigation/AppBreadcrumbs';
import IncidentMetricStrip from '../Components/Shared/IncidentMetricStrip';
import CurrentRosterViewBar from '../Components/Shared/CurrentRosterViewBar';
import IncidentInfoBar from '../Components/Incidents/IncidentInfoBar';
import ClosedIncidentBanner from '../Components/Incidents/ClosedIncidentBanner';
import RosterPersonRow from '../Components/Headcount/RosterPersonRow';
import HeadcountFilterDrawer from '../Components/Headcount/HeadcountFilterDrawer';
import HeadcountActiveFilters from '../Components/Headcount/HeadcountActiveFilters';
import FiltersButton from '../Components/Elements/FiltersButton';
import { AlphaRange, personMatchesAlphaRange, getLastName } from '../Utils/alphaRangeHelpers';
import { getPersonTypeLabel } from '../Utils/personTypeLabels';
import { formatTime as formatTimeHelper } from '../Utils/dateHelpers';
import { isIncidentClosed, canEditClosedRoster } from '../Utils/incidentHelpers';
import { getUiPersonStatus, getStatusDisplayLabel } from '../Utils/headcountStatus';
import { personMatchesSourceFilter, personMatchesPersonType } from '../Utils/personHelpers';
import type { SourceFilter } from '../Utils/personHelpers';
import { PersonType } from '../Types/enums';
import { buildTenantCompanyOptions, applyTenantCompanyFilter } from '../Utils/tenantCompanyHelpers';
import { useSyncStatus } from '../Hooks/useSyncStatus';
import SyncStatusIndicator from '../Components/Elements/SyncStatusIndicator';
import {
  getNeedsAttentionPageState,
  getNeedsAttentionEmptyState,
  getNeedsAttentionQuickActions,
  getMetricColor,
  NeedsAttentionCounts,
  NeedsAttentionPageState,
} from '../Utils/needsAttentionState';
import { deriveScopeCountsFromSummary } from '../features/incidents/accountabilityScope';
import { useIncidentRealtime } from '../Hooks/useIncidentRealtime';
import type {
  RosterPersonStatusChangedEvent,
  IncidentClosedEvent,
  IncidentRefreshRequestedEvent,
  RealtimeConnectionState,
} from '../realtime/incidentRealtimeTypes';
import { isValidHeadcountStatus } from '../realtime/incidentRealtimeHelpers';
import { useHeadcountNavigationGuard } from '../Hooks/useHeadcountNavigationGuard';
import HeadcountSaveBeforeLeaveDialog from '../Components/Headcount/HeadcountSaveBeforeLeaveDialog';
import { useSecurity } from '../Contexts/SecurityProvider';
import { useIncidentAssignments } from '../Hooks/useIncidentAssignments';
import { useHeadcountPendingSync, FlushResult } from '../Hooks/useHeadcountPendingSync';
import { useLinkedAccounting } from '../Hooks/useLinkedAccounting';
import LinkedAccountingDialog from '../Components/Headcount/LinkedAccountingDialog';

const AUTO_REFRESH_MS = 5_000;
const AUTO_SYNC_MS = 5_000;
const AUTO_REFRESH_REALTIME_MS = 60_000;

const MissingUnaccountedPage: React.FC = () => {
  const { incidentId } = useParams<{ incidentId: string }>();
  const navigate = useNavigate();

  // ── Data state ──
  const [notAccountedPeople, setNotAccountedPeople] = useState<IncidentRosterPerson[]>([]);
  const [incidentName, setIncidentName] = useState('');
  const [incidentLocation, setIncidentLocation] = useState('');
  const [incidentType, setIncidentType] = useState<string | null | undefined>(undefined);
  const [incidentStatus, setIncidentStatus] = useState<string | undefined>(undefined);
  const [startedAt, setStartedAt] = useState('');
  const [totalRosterCount, setTotalRosterCount] = useState(0);
  const [accountedCount, setAccountedCount] = useState(0);
  const [notCheckedCount, setNotCheckedCount] = useState(0);
  const [fullRosterTotal, setFullRosterTotal] = useState(0);
  const [assignedOfficeNoBadgeCount, setAssignedOfficeNoBadgeCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);

  // ── Pending batch updates ──

  const flushRef = useRef<() => Promise<FlushResult>>(() => Promise.resolve({ success: true, flushedCount: 0 }));

  // ── Online/sync state ──
  const syncStatus = useSyncStatus();

  // ── Permissions ──
  const { hasAnyRole, security } = useSecurity();

  // ── Filters ──
  const [searchQuery, setSearchQuery] = useState('');
  const [alphaRange, setAlphaRange] = useState<AlphaRange>('All');
  const [personTypeFilter, setPersonTypeFilter] = useState<string[]>([]);
  const [rosterContextFilters, setRosterContextFilters] = useState<SourceFilter[]>([]);
  const [tenantCompanyFilter, setTenantCompanyFilter] = useState<string[]>([]);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const syncTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [realtimeSnackbar, setRealtimeSnackbar] = useState<{ open: boolean; message: string }>({ open: false, message: '' });

  // ── Load data ──
  const loadData = useCallback(async (showSpinner = true) => {
    if (!incidentId) return;
    if (showSpinner) setLoading(true);
    setError(null);
    try {
      const [unaccResult, summaryResult, detailResult] = await Promise.all([
        getUnaccountedPeople(incidentId),
        getIncidentSummary(incidentId).catch(() => null),
        getIncidentDetail(incidentId).catch(() => null),
      ]);
      setNotAccountedPeople(unaccResult.notAccounted);
      if (unaccResult.serverTimeUtc) setLastUpdated(unaccResult.serverTimeUtc);
      const sum = summaryResult?.summary;
      setIncidentName(sum?.incidentName || detailResult?.incidentName || '');
      setIncidentLocation(sum?.location || detailResult?.location || '');
      setIncidentType(sum?.incidentType !== undefined ? sum.incidentType : detailResult?.incidentType);
      setIncidentStatus(sum?.status ?? detailResult?.status);
      setStartedAt(sum?.startedAtUtc || detailResult?.startedAtUtc || '');
      // Derive accountability-scoped counts from summary
      if (sum) {
        const sc = deriveScopeCountsFromSummary(sum);
        setTotalRosterCount(sc.accountabilityCounts.total);
        setAccountedCount(sc.accountabilityCounts.accounted);
        setNotCheckedCount(sc.accountabilityCounts.notChecked);
        setFullRosterTotal(sc.fullRosterCounts.total);
        setAssignedOfficeNoBadgeCount(sc.assignedOfficeNoBadgeTodayNotChecked ?? sc.assignedOfficeNoBadgeTodayCount ?? 0);
      } else {
        setTotalRosterCount(0);
        setAccountedCount(0);
        setNotCheckedCount(0);
        setFullRosterTotal(0);
        setAssignedOfficeNoBadgeCount(0);
      }
      if (summaryResult?.serverTimeUtc) setLastUpdated(summaryResult.serverTimeUtc);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data.');
    } finally {
      setLoading(false);
    }
  }, [incidentId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Incident closed check ──
  const isClosed = isIncidentClosed({ status: incidentStatus });

  // ── Closed-incident roster edit permission ──
  const { assignments } = useIncidentAssignments(isClosed ? incidentId : undefined);
  const closedRosterEditable = isClosed && canEditClosedRoster({
    hasAnyRole,
    currentUserId: security?.appUserId,
    assignments,
  });
  const actionsDisabled = (isClosed && !closedRosterEditable) || !syncStatus.isOnline;

  const {
    pendingUpdates, syncError, markStatusPending, flushPendingStatusUpdates,
    discardPendingStatusUpdates, clearSyncError, hasPendingChanges, isSyncing,
  } = useHeadcountPendingSync({
    incidentId,
    onSyncStart: () => syncStatus.markSaving(),
    onSyncSuccess: (serverTimeUtc) => syncStatus.markSynced(serverTimeUtc),
    onSyncError: (message) => syncStatus.markError(message),
    onRosterUpdate: setNotAccountedPeople,
    onRefreshRoster: () => loadData(false),
  });

  // ── Realtime SignalR ──
  const handleRealtimeStatusChanged = useCallback((event: RosterPersonStatusChangedEvent) => {
    if (!isValidHeadcountStatus(event.effectiveHeadcountStatus)) return;
    // Events omit source evidence; refresh it before claiming badge/sign-in context.
    if (event.effectiveHeadcountStatus === 'notAccounted') void loadData(false);

    // Update the not-accounted list
    setNotAccountedPeople((prev) => {
      if (event.effectiveHeadcountStatus === 'notAccounted') {
        // Person became notAccounted — add/update in the list
        const exists = prev.find((p) => p.personId === event.personId);
        if (exists) {
          return prev.map((p) => p.personId === event.personId ? {
            ...p,
            status: event.effectiveHeadcountStatus,
            lastUpdatedAtUtc: event.lastUpdatedAtUtc,
            lastUpdatedByName: event.lastUpdatedByDisplayName ?? p.lastUpdatedByName,
          } : p);
        }
        // New person in the list — add with available event data
        return [...prev, {
          personId: event.personId,
          displayName: event.displayName ?? '',
          email: event.email ?? undefined,
          phone: event.phone ?? undefined,
          personType: event.personType,
          status: event.effectiveHeadcountStatus,
          lastUpdatedAtUtc: event.lastUpdatedAtUtc,
          lastUpdatedByName: event.lastUpdatedByDisplayName ?? undefined,
        }];
      }
      // Person is no longer notAccounted — remove from list
      return prev.filter((p) => p.personId !== event.personId);
    });

    // Update counts from event (authoritative — full roster counts)
    // Derive accountability-scoped counts using last-known assignedOfficeNoBadge
    if (event.counts) {
      setFullRosterTotal(event.counts.total);
      setAssignedOfficeNoBadgeCount((prevNoBadge) => {
        const accountabilityTotal = Math.max(0, event.counts.total - prevNoBadge);
        const accountabilityNotChecked = Math.max(0, accountabilityTotal - event.counts.accounted - event.counts.notAccounted);
        setTotalRosterCount(accountabilityTotal);
        setAccountedCount(event.counts.accounted);
        setNotCheckedCount(accountabilityNotChecked);
        return prevNoBadge;
      });
    }

    syncStatus.markSynced(event.lastUpdatedAtUtc);
  }, [syncStatus, loadData]);

  const handleRealtimeIncidentClosed = useCallback((event: IncidentClosedEvent) => {
    setIncidentStatus('closed');
    setRealtimeSnackbar({
      open: true,
      message: event.closedByDisplayName
        ? `Incident closed by ${event.closedByDisplayName}`
        : 'Incident has been closed',
    });
  }, []);

  const handleRealtimeRefreshRequested = useCallback((_event: IncidentRefreshRequestedEvent) => {
    loadData(false);
  }, [loadData]);

  const handleRealtimeReconnect = useCallback(() => {
    loadData(false);
    syncStatus.markSynced();
  }, [loadData, syncStatus]);

  const handleRealtimeConnectionStateChanged = useCallback((state: RealtimeConnectionState) => {
    if (state === 'reconnecting') {
      syncStatus.markReconnecting();
    } else if (state === 'connected') {
      syncStatus.markSynced();
    }
  }, [syncStatus]);

  const { connectionState: realtimeState } = useIncidentRealtime({
    incidentId,
    enabled: !loading && !isClosed,
    onRosterPersonStatusChanged: handleRealtimeStatusChanged,
    onIncidentClosed: handleRealtimeIncidentClosed,
    onIncidentRefreshRequested: handleRealtimeRefreshRequested,
    onReconnect: handleRealtimeReconnect,
    onConnectionStateChanged: handleRealtimeConnectionStateChanged,
  });

  const realtimeConnected = realtimeState === 'connected';

  // ── Auto-refresh (only for active incidents or authorized closed-incident editors) ──
  // Use slower interval when realtime is connected
  useEffect(() => {
    if (isClosed && !closedRosterEditable) return;
    const interval = realtimeConnected ? AUTO_REFRESH_REALTIME_MS : AUTO_REFRESH_MS;
    refreshTimerRef.current = setInterval(() => loadData(false), interval);
    return () => { if (refreshTimerRef.current) clearInterval(refreshTimerRef.current); };
  }, [loadData, isClosed, closedRosterEditable, realtimeConnected]);

  // ── Effective status ──
  const getEffectiveStatus = useCallback(
    (person: IncidentRosterPerson): PersonStatus => {
      const pending = pendingUpdates.get(person.personId);
      return pending ? pending.status : getUiPersonStatus(person);
    },
    [pendingUpdates],
  );

  // ── Handle status change (disabled for closed unless authorized) ──
  const linkedAccounting = useLinkedAccounting({
    incidentId, disabled: actionsDisabled, queueStatus: markStatusPending, getEffectiveStatus,
  });
  const handleStatusChange = linkedAccounting.changeStatus;
  // Keep a stable ref so the setInterval callback always calls the latest flush
  flushRef.current = () => flushPendingStatusUpdates('timer');

  // Legacy wrapper for manual retry
  const syncPending = useCallback(async () => {
    await flushPendingStatusUpdates('timer');
  }, [flushPendingStatusUpdates]);

  // ── Cancel / discard helpers for navigation guard ──
  const cancelDelayedSync = useCallback(() => {
    if (syncTimerRef.current) {
      clearInterval(syncTimerRef.current);
      syncTimerRef.current = null;
    }
  }, []);

  // ── Auto-sync timer (only for active or authorized closed-incident editors) ──
  useEffect(() => {
    if (isClosed && !closedRosterEditable) return;
    syncTimerRef.current = setInterval(() => flushRef.current(), AUTO_SYNC_MS);
    return () => { if (syncTimerRef.current) clearInterval(syncTimerRef.current); };
  }, [isClosed, closedRosterEditable]);

  // ── Navigation guard (sync before leave) ──
  const navGuard = useHeadcountNavigationGuard({
    hasPendingChanges,
    isSyncing,
    isOnline: syncStatus.isOnline,
    flushPendingStatusUpdates,
    cancelDelayedSync,
    discardPendingStatusUpdates,
  });

  // ── Build effective not-accounted list (with pending overrides) ──
  const effectiveNotAccounted = useMemo(() => {
    return notAccountedPeople.filter((person) => {
      const effective = getEffectiveStatus(person);
      return effective === 'notAccounted';
    });
  }, [notAccountedPeople, getEffectiveStatus]);

  const totalRemaining = effectiveNotAccounted.length;

  // ── Counts ──
  const counts: NeedsAttentionCounts = useMemo(() => ({
    total: totalRosterCount,
    accounted: accountedCount,
    notAccounted: totalRemaining,
    notChecked: notCheckedCount,
  }), [totalRosterCount, accountedCount, totalRemaining, notCheckedCount]);

  // ── Page state ──
  const pageState: NeedsAttentionPageState = useMemo(
    () => getNeedsAttentionPageState(counts, incidentStatus),
    [counts, incidentStatus],
  );
  const emptyContent = useMemo(() => getNeedsAttentionEmptyState(pageState, counts), [pageState, counts]);
  const quickActions = useMemo(
    () => incidentId ? getNeedsAttentionQuickActions(pageState, counts, incidentId) : [],
    [pageState, counts, incidentId],
  );

  // ── Person type & roster context counts for filter drawer ──
  const personTypeCounts = useMemo(() => {
    const c: Record<string, number> = {};
    effectiveNotAccounted.forEach((p) => {
      if (p.isUnlisted) { c['unlisted'] = (c['unlisted'] ?? 0) + 1; return; }
      const pt = (p.personType ?? '').toLowerCase().trim();
      if (pt) c[pt] = (c[pt] ?? 0) + 1;
    });
    return c;
  }, [effectiveNotAccounted]);

  const rosterContextCounts = useMemo(() => {
    const c: Record<string, number> = { badgeData: 0, lobbyTrack: 0, assignedOffice: 0, assignedOfficeNoBadge: 0, manualAdd: 0 };
    effectiveNotAccounted.forEach((p) => {
      if (personMatchesSourceFilter(p, 'badgeData')) c.badgeData++;
      if (personMatchesSourceFilter(p, 'lobbyTrack')) c.lobbyTrack++;
      if (personMatchesSourceFilter(p, 'assignedOffice')) c.assignedOffice++;
      if (personMatchesSourceFilter(p, 'assignedOfficeNoBadge')) c.assignedOfficeNoBadge++;
      if (personMatchesSourceFilter(p, 'manualAdd')) c.manualAdd++;
    });
    return c;
  }, [effectiveNotAccounted]);

  // Tenant company options and counts
  const tenantCompanyOptions = useMemo(
    () => buildTenantCompanyOptions(effectiveNotAccounted, tenantCompanyFilter),
    [effectiveNotAccounted, tenantCompanyFilter],
  );
  const totalTenantCount = useMemo(
    () => effectiveNotAccounted.filter((p) => (p.personType ?? '').toLowerCase().trim() === PersonType.Tenant).length,
    [effectiveNotAccounted],
  );
  const tenantCompanyLabels = useMemo(() => {
    const map: Record<string, string> = {};
    tenantCompanyOptions.forEach((o) => { map[o.key] = o.label; });
    return map;
  }, [tenantCompanyOptions]);

  // ── Filter the not-accounted list ──
  const unifiedList = useMemo(() => {
    let pool = effectiveNotAccounted;

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      pool = pool.filter((p) =>
        p.displayName.toLowerCase().includes(q) ||
        p.email?.toLowerCase().includes(q) ||
        p.phone?.toLowerCase().includes(q) ||
        p.companyName?.toLowerCase().includes(q) ||
        p.practice?.toLowerCase().includes(q) ||
        p.personType?.toLowerCase().includes(q) ||
        p.floor?.toLowerCase().includes(q) ||
        p.location?.toLowerCase().includes(q) ||
        p.assignedOfficeLocationName?.toLowerCase().includes(q) ||
        p.officeCubeNumber?.toLowerCase().includes(q) ||
        p.tenantName?.toLowerCase().includes(q) ||
        p.lastIncidentDayBadgeActivity?.doorName?.toLowerCase().includes(q) ||
        p.lastIncidentDayBadgeActivity?.locationName?.toLowerCase().includes(q),
      );
    } else if (alphaRange !== 'All') {
      pool = pool.filter((p) => personMatchesAlphaRange(p, alphaRange));
    }

    if (personTypeFilter.length > 0) {
      pool = pool.filter((p) => personMatchesPersonType(p, personTypeFilter));
    }
    if (rosterContextFilters.length > 0) {
      pool = pool.filter((p) =>
        rosterContextFilters.some((sf) => personMatchesSourceFilter(p, sf)),
      );
    }
    if (tenantCompanyFilter.length > 0) {
      pool = applyTenantCompanyFilter(pool, tenantCompanyFilter);
    }

    return [...pool].sort((a, b) =>
      getLastName(a).localeCompare(getLastName(b)) || a.displayName.localeCompare(b.displayName),
    );
  }, [effectiveNotAccounted, alphaRange, searchQuery, personTypeFilter, rosterContextFilters, tenantCompanyFilter]);

  const formatLastUpdated = (utc?: string | null) => {
    if (!utc) return '';
    const t = formatTimeHelper(utc);
    return t ? 'Last updated: ' + t : '';
  };

  // ── Export handler ──
  const handleExportFiltered = useCallback(() => {
    if (unifiedList.length === 0) return;
    const esc = (val: unknown): string => {
      if (val == null) return '';
      const str = String(val);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) return `"${str.replace(/"/g, '""')}"`;
      return str;
    };
    const header = ['Name', 'Email', 'Phone', 'Person Type', 'Practice', 'Floor', 'Location', 'Status', 'Last Updated', 'Updated By'];
    const rows = unifiedList.map((p) => [
      esc(p.displayName), esc(p.email), esc(p.phone), esc(p.personType), esc(p.practice),
      esc(p.floor), esc(p.location), getStatusDisplayLabel(getEffectiveStatus(p)),
      esc(p.lastUpdatedAtUtc), esc(p.lastUpdatedByName),
    ]);
    const csv = [header.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(incidentName || 'incident').replace(/[^a-zA-Z0-9]/g, '_')}_not_accounted.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [unifiedList, incidentName, getEffectiveStatus]);

  // ── Active filter count for Filters button ──
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (alphaRange !== 'All') count++;
    count += personTypeFilter.length;
    count += rosterContextFilters.length;
    count += tenantCompanyFilter.length;
    return count;
  }, [alphaRange, personTypeFilter, rosterContextFilters, tenantCompanyFilter]);

  const handleClearAllFilters = useCallback(() => {
    setAlphaRange('All');
    setPersonTypeFilter([]);
    setRosterContextFilters([]);
    setTenantCompanyFilter([]);
    setSearchQuery('');
  }, []);

  // ── CurrentRosterViewBar derived display (from the same state as the list) ──
  const activeGroupLabel = alphaRange !== 'All' && !searchQuery.trim()
    ? alphaRange.replace('-', '–')
    : null;

  const otherFilterSummary = useMemo(() => {
    const parts: string[] = [];
    if (searchQuery.trim()) parts.push(`Search: "${searchQuery.trim()}"`);
    if (personTypeFilter.length > 0) {
      parts.push(`Person Type: ${personTypeFilter.map((p) => getPersonTypeLabel(p) ?? p).join(', ')}`);
    }
    if (tenantCompanyFilter.length > 0) {
      parts.push(`Tenant Company: ${tenantCompanyFilter.map((k) => tenantCompanyLabels[k] ?? k).join(', ')}`);
    }
    if (rosterContextFilters.length > 0) {
      parts.push(`Roster Context: ${rosterContextFilters.length}`);
    }
    return parts.length > 0 ? parts.join(' · ') : null;
  }, [searchQuery, personTypeFilter, tenantCompanyFilter, tenantCompanyLabels, rosterContextFilters]);

  const isRosterNarrowed = activeFilterCount > 0 || !!searchQuery.trim();

  // ── Render ──
  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2, color: appColors.text.body }}>Loading{'\u2026'}</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Alert severity="error" sx={{ mb: 2, justifyContent: 'center' }}>{error}</Alert>
        <Button variant="contained" onClick={() => loadData()}>Retry</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 1, px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 }, maxWidth: 1200, mx: 'auto', width: '100%' }}>
      {/* ── Breadcrumbs ── */}
      <AppBreadcrumbs items={[
        { label: 'Incidents', path: '/' },
        { label: incidentName || 'Incident', path: `/incidents/${incidentId}/incidentdashboard` },
        { label: 'Not Accounted' },
      ]} />

      {/* ── Closed banner ── */}
      {pageState === 'closed' && !closedRosterEditable && <ClosedIncidentBanner />}
      {hasPendingChanges && <Alert severity={syncError ? 'warning' : 'info'} sx={{ mb: 2 }}>
        {pendingUpdates.size} {pendingUpdates.size === 1 ? 'record has' : 'records have'} pending changes awaiting server confirmation.
      </Alert>}

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: appColors.text.heading }}>
            Not Accounted
          </Typography>
          <Typography variant="body2" sx={{ color: appColors.text.body, mt: 0.25 }}>
            People currently marked Not Accounted during this incident.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          {(!isClosed || closedRosterEditable) && (
            <SyncStatusIndicator
              state={syncStatus.state}
              lastSyncedAt={syncStatus.lastSyncedAt}
              errorMessage={syncStatus.errorMessage}
              onRetry={syncPending}
            />
          )}
          {lastUpdated && (
            <Typography variant="caption" sx={{ color: appColors.text.placeholder }}>
              {formatLastUpdated(lastUpdated)}
            </Typography>
          )}
          <IconButton size="small" onClick={() => loadData(false)} aria-label="Refresh data" sx={{ color: appColors.text.body }}>
            <RefreshIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </Box>

      {/* ── Incident Info ── */}
      {incidentName && (
        <IncidentInfoBar
          incidentName={incidentName}
          incidentType={incidentType}
          location={incidentLocation}
          startedAtUtc={startedAt}
          status={incidentStatus}
        />
      )}

      {/* ── Current roster view bar ── */}
      <CurrentRosterViewBar
        scopeLabel="Needs Attention"
        count={unifiedList.length}
        countNoun="need attention"
        groupLabel={activeGroupLabel}
        filterSummary={otherFilterSummary}
        isNarrowed={isRosterNarrowed}
        onReset={handleClearAllFilters}
      />

      {/* ── Summary cards ── */}
      <Box sx={{ mb: 2.5 }}>
        <IncidentMetricStrip
          metrics={[
            {
              key: 'notAccounted',
              icon: <NotAccountedIcon sx={{ fontSize: 24 }} />,
              label: 'Needs Attention',
              value: counts.notAccounted,
              description: counts.notAccounted > 0 ? 'Marked Not Accounted' : 'Everyone accounted for',
              variant: 'notAccounted',
              neutralWhenZero: true,
              ariaLabel: `${counts.notAccounted} people need attention, marked Not Accounted`,
            },
            {
              key: 'accounted',
              icon: <AccountedIcon sx={{ fontSize: 24 }} />,
              label: 'Accounted For',
              value: counts.accounted,
              description: counts.total > 0 ? `${Math.round((counts.accounted / counts.total) * 100)}% of accountability roster` : 'People accounted',
              variant: 'accounted',
              ariaLabel: `${counts.accounted} people accounted for`,
            },
            {
              key: 'toAccount',
              icon: <TotalIcon sx={{ fontSize: 24 }} />,
              label: 'People in Scope',
              value: counts.total,
              description: 'Accountability roster',
              variant: 'toAccount',
              ariaLabel: `People in scope, ${counts.total} in the accountability roster`,
            },
          ]}
          contextMetrics={[
            {
              key: 'fullRoster',
              label: 'Full Roster',
              value: fullRosterTotal,
              ariaLabel: `Full Roster, ${fullRosterTotal} people`,
            },
            {
              key: 'noBadgeToday',
              label: 'No Badge Today',
              value: assignedOfficeNoBadgeCount,
              ariaLabel: `No Badge Today, ${assignedOfficeNoBadgeCount} people`,
            },
          ]}
        />
      </Box>

      {/* ── Action guidance banner (only when notAccounted > 0) ── */}
      {counts.notAccounted > 0 && pageState === 'needsAttention' && (
        <Box
          role="status"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 1.25,
            bgcolor: '#FFF3E0',
            border: `1px solid ${appColors.status.warning}`,
            borderRadius: '8px',
            mb: 2,
          }}
        >
          <Typography variant="body2" sx={{ color: appColors.text.heading, fontWeight: 500, flex: 1 }}>
            <strong>{counts.notAccounted} {counts.notAccounted === 1 ? 'person requires' : 'people require'} attention.</strong>{' '}
            Contact or review each person, then mark them Accounted when resolved.
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate(`/incidents/${incidentId}/headcount`)}
            sx={{ textTransform: 'none', fontWeight: 600, borderColor: appColors.status.warning, color: appColors.status.warning, whiteSpace: 'nowrap', '&:hover': { borderColor: appColors.status.warning, bgcolor: '#FFF8E1' } }}
          >
            Continue Headcount
          </Button>
        </Box>
      )}

      {/* ── State-aware empty state (when no one is notAccounted) ── */}
      {pageState !== 'needsAttention' && (
        <Box sx={{ p: 4, textAlign: 'center', bgcolor: appColors.surface.card, borderRadius: '12px', border: `1px solid ${appColors.border.default}`, mb: 2.5 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              mb: 0.5,
              color: pageState === 'complete' ? appColors.status.success : appColors.text.heading,
            }}
          >
            {emptyContent.title}
          </Typography>
          <Typography variant="body2" sx={{ color: appColors.text.caption, mb: 2 }}>
            {emptyContent.body}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
            {quickActions.map((qa) => (
              <Button
                key={qa.label}
                variant={qa.variant}
                size="small"
                onClick={() => navigate(qa.route)}
                sx={{
                  fontWeight: 600,
                  borderRadius: '8px',
                  textTransform: 'none',
                  ...(qa.highlight ? { bgcolor: appColors.button.dark, color: '#FFFFFF', '&:hover': { bgcolor: appColors.button.darkHover } } : {}),
                }}
              >
                {qa.label}
              </Button>
            ))}
          </Box>
        </Box>
      )}

      {/* ── Toolbar: Search + Export + Filters (only when notAccounted > 0) ── */}
      {totalRemaining > 0 && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search people marked Not Accounted..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search people marked Not Accounted"
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 20, color: appColors.text.placeholder }} />
                    </InputAdornment>
                  ),
                },
              }}
              sx={{
                flex: 1,
                minWidth: { xs: '100%', sm: 200 },
                maxWidth: 500,
                '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: appColors.surface.card, minHeight: 44 },
              }}
            />
            <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ExportIcon sx={{ fontSize: 18 }} />}
                onClick={handleExportFiltered}
                aria-label={`Export Not Accounted list, ${totalRemaining} people`}
                sx={{
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 600,
                  minHeight: 44,
                  borderColor: appColors.primary.main,
                  color: appColors.primary.main,
                  '&:hover': { borderColor: appColors.primary.main, bgcolor: `${appColors.primary.main}08` },
                }}
              >
                Export Not Accounted List
              </Button>
              <FiltersButton onClick={() => setFilterDrawerOpen(true)} activeFilterCount={activeFilterCount} />
            </Box>
          </Box>

          {/* ── Active filter chips + summary line ── */}
          <HeadcountActiveFilters
            filteredCount={unifiedList.length}
            sortMode="lastNameAsc"
            alphaFilter={alphaRange}
            personTypeFilters={personTypeFilter}
            rosterContextFilters={rosterContextFilters}
            tenantCompanyFilters={tenantCompanyFilter}
            tenantCompanyLabels={tenantCompanyLabels}
            onRemoveAlpha={() => setAlphaRange('All')}
            onRemovePersonType={(v) => {
              setPersonTypeFilter((prev) => prev.filter((x) => x !== v));
              if (v === PersonType.Tenant) setTenantCompanyFilter([]);
            }}
            onRemoveRosterContext={(v) => setRosterContextFilters((prev) => prev.filter((x) => x !== v))}
            onRemoveTenantCompany={(v) => setTenantCompanyFilter((prev) => prev.filter((x) => x !== v))}
            onClearAll={handleClearAllFilters}
          />

          {/* ── Filter drawer ── */}
          <HeadcountFilterDrawer
            open={filterDrawerOpen}
            onClose={() => setFilterDrawerOpen(false)}
            alphaFilter={alphaRange}
            onAlphaFilterChange={setAlphaRange}
            personTypeFilters={personTypeFilter}
            onPersonTypeFiltersChange={(v) => {
              setPersonTypeFilter(v);
              if (!v.includes(PersonType.Tenant)) setTenantCompanyFilter([]);
            }}
            rosterContextFilters={rosterContextFilters}
            onRosterContextFiltersChange={setRosterContextFilters}
            onClearAll={() => { setAlphaRange('All'); setPersonTypeFilter([]); setRosterContextFilters([]); setTenantCompanyFilter([]); }}
            personTypeCounts={personTypeCounts}
            rosterContextCounts={rosterContextCounts}
            filteredCount={unifiedList.length}
            tenantCompanyFilters={tenantCompanyFilter}
            onTenantCompanyFiltersChange={(v) => {
              setTenantCompanyFilter(v);
              if (v.length > 0 && !personTypeFilter.includes(PersonType.Tenant)) {
                setPersonTypeFilter((prev) => [...prev, PersonType.Tenant]);
              }
            }}
            tenantCompanyOptions={tenantCompanyOptions}
            totalTenantCount={totalTenantCount}
          />
        </>
      )}

      {/* ── Not Accounted person list (only when notAccounted > 0) ── */}
      {totalRemaining > 0 && (
        <>
          <Box sx={{ bgcolor: appColors.surface.card, borderRadius: '12px', border: `1px solid ${appColors.border.default}`, overflow: 'hidden', mb: 2.5 }}>
            {/* List header */}
            <Box sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${appColors.border.default}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: appColors.text.heading }}>
                People Requiring Attention
              </Typography>
              <Typography variant="caption" sx={{ color: appColors.text.placeholder }}>
                {unifiedList.length} {unifiedList.length === 1 ? 'person' : 'people'}
              </Typography>
            </Box>

            {unifiedList.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography sx={{ color: appColors.text.caption, mb: 1.5 }}>
                  No people match the current search or filters.
                </Typography>
                <Button
                  size="small"
                  onClick={handleClearAllFilters}
                  sx={{ textTransform: 'none', fontWeight: 600, color: appColors.primary.main }}
                >
                  Clear filters
                </Button>
              </Box>
            ) : (
              unifiedList.map((person, idx) => (
                <RosterPersonRow
                  key={person.personId}
                  person={person}
                  effectiveStatus={getEffectiveStatus(person)}
                  isPending={pendingUpdates.has(person.personId)}
                  onStatusChange={actionsDisabled ? () => {} : handleStatusChange}
                  avatarColorIndex={idx}
                  readOnly={actionsDisabled}
                />
              ))
            )}
          </Box>

          {/* ── Quick actions for needsAttention state ── */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            {quickActions.map((qa) => (
              <Button
                key={qa.label}
                variant={qa.variant}
                size="small"
                onClick={() => navigate(qa.route)}
                sx={{
                  fontWeight: 600,
                  borderRadius: '8px',
                  textTransform: 'none',
                  ...(qa.highlight ? { bgcolor: appColors.button.dark, color: '#FFFFFF', '&:hover': { bgcolor: appColors.button.darkHover } } : {}),
                }}
              >
                {qa.label}
              </Button>
            ))}
          </Box>
        </>
      )}

      {/* ── Save-before-leave dialog ── */}
      <HeadcountSaveBeforeLeaveDialog
        open={navGuard.showDialog}
        state={navGuard.dialogState}
        pendingCount={pendingUpdates.size}
        errorMessage={navGuard.dialogError}
        onRetry={navGuard.handleRetry}
        onStay={navGuard.handleStay}
        onDiscard={navGuard.handleDiscard}
      />
      <LinkedAccountingDialog
        request={linkedAccounting.request}
        disabled={actionsDisabled}
        onConfirm={linkedAccounting.confirm}
        onCancel={linkedAccounting.cancel}
        onRetry={linkedAccounting.retry}
      />

      {/* ── Sync error snackbar ── */}
      <Snackbar
        open={!!syncError}
        autoHideDuration={6000}
        onClose={clearSyncError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="warning" onClose={clearSyncError} sx={{ width: '100%' }}>
          {syncError}
        </Alert>
      </Snackbar>

      {/* ── Realtime event snackbar ── */}
      <Snackbar
        open={realtimeSnackbar.open}
        autoHideDuration={4000}
        onClose={() => setRealtimeSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="info" onClose={() => setRealtimeSnackbar((prev) => ({ ...prev, open: false }))} sx={{ width: '100%' }}>
          {realtimeSnackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MissingUnaccountedPage;
